<template>
  <div class="dynamic-accordion">
    <div
      v-for="(panel, index) of panels"
      :key="index"
      class="dynamic-accordion__panel"
    >
      <button
        v-if="!isDescriptionPanel(panel)"
        class="dynamic-accordion__toggle"
        :class="[
          isActive(index) ? $class('active') : '',
          isDescriptionPanel(panel) ? 'dynamic-accordion__toggle--description' : ''
        ]"
        :aria-expanded="isActive(index)"
        type="button"
        :disabled="isDescriptionPanel(panel)"
        @click="handlePanelClick(panel, index)"
      >
        <component
          :is="headingType"
          class="dynamic-accordion__heading"
          :class="panel.classes?.title"
          v-text="panel.title"
        />

        <!-- Only show icon for non-description panels -->
        <component
          :is="isActive(index) ? 'icon-minus' : 'icon-plus'"
          v-if="!panel.title || !panel.title.toLowerCase().includes('description')"
        />
      </button>

      <transition-height>
        <div
          v-if="isActive(index)"
          class="dynamic-accordion__content"
          :class="panel.classes?.content"
        >
          <div
            v-if="panel.useReadMore"
            class="dynamic-accordion__read-more-wrapper text-body-s"
          >
            <span
              class="dynamic-accordion__read-more-content text-body-s"
              v-html="getPanelContent(panel)"
            />
          </div>
          <div
            v-else
            v-html="panel.content"
          />
        </div>
      </transition-height>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Dynamic Accordion (dynamic-accordion)
 * -----------------------------------------------------------------------------
 * Renders a set of dynamic-accordion panels that can be opened and closed.
 *
 * @param {String} [headingType] - Element type for the toggle label.
 * @param {Array} panelData - Array of panel objects.
 * @param {String} panelData.content - Panel content.
 * @param {String} panelData.title - Title of the panel, used as a label for the
 * toggle button.
 * @param {Object} panelData.classes - Additional classes to use.
 */
import { mapState } from 'vuex'

import TransitionHeight from '~global/transition-height/transition-height'

import IconMinus from '~icons/misc/minus.svg'
import IconPlus from '~icons/misc/plus.svg'

export default {
  name: 'DynamicAccordion',

  components: {
    IconMinus,
    IconPlus,
    TransitionHeight,
  },

  props: {
    headingType: {
      type: String,
      default: 'span',
    },
    panelData: {
      type: Array,
      default: () => [],
    },
    expandedByDefault: {
      type: Boolean,
      default: false,
    },
    expandDescriptionOnly: {
      type: Boolean,
      default: true,
    },
    maxCharactersMobile: {
      type: Number,
      default: 80,
    },
    maxCharactersDesktop: {
      type: Number,
      default: 127,
    },
  },

  data() {
    return {
      panels: [],
      resizeTimeout: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Get max characters based on viewport
     */
    maxCharacters() {
      return this.isMobile ? this.maxCharactersMobile : this.maxCharactersDesktop
    },
  },

  mounted() {

    /**
     * Store panels on the component with a toggleable 'active' property.
     * - Also adds default title class.
     */
    this.panels = this.panelData.map((panel) => {

      const isDescriptionPanel =
        panel.title &&
        panel.title.toLowerCase().includes('description')

      const processedPanel = {
        active: (this.expandDescriptionOnly && isDescriptionPanel) || this.expandedByDefault,
        classes: {
          title: 'text-heading-2xs',
        },
        ...panel,
        isExpanded: false,
      }

      // Process truncation if useReadMore is enabled
      if (panel.useReadMore) {
        this.processTruncation(processedPanel)
      }

      return processedPanel
    })

    window.addEventListener('resize', this.handleResize)
    this.$nextTick().then(() => {
      this.$el.addEventListener('click', this.handleReadMoreButtonClick)
    })
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    clearTimeout(this.resizeTimeout)
    if (this.$el) {
      this.$el.removeEventListener('click', this.handleReadMoreButtonClick)
    }

  },

  methods: {

    /**
     * Check if panel is a description panel
     * @param {Object} panel - Panel object
     * @returns {Boolean}
     */
    isDescriptionPanel(panel) {
      return panel.title && panel.title.toLowerCase().includes('description')
    },

    /**
     * Handle panel click
     * @param {Object} panel - Panel object
     * @param {Number} index - Panel index
     */
    handlePanelClick(panel, index) {
      if (!this.isDescriptionPanel(panel)) {
        this.togglePanel(index)
      }
    },

    /**
     * Inject read less button into last <p> of HTML content
     * @param {String} html - HTML content
     * @returns {String}
     */
    injectReadLessButton(html) {
      const readLessBtn = `
        <span
          role="button"
          tabindex="0"
          class="dynamic-accordion__read-more-toggle text-body-s"
        >${this.$string('accessibility.read_less')}</span>
      `
      const lastPIndex = html.lastIndexOf('</p>')
      if (lastPIndex !== -1) {
        return html.slice(0, lastPIndex) + readLessBtn + html.slice(lastPIndex)
      }
      return html + readLessBtn
    },

    /**
     * Handle click on dynamically injected read more button
     */
    handleReadMoreButtonClick(event) {
      if (
        event.target.matches('.dynamic-accordion__read-more-toggle') &&
        event.target.closest('.dynamic-accordion__panel')
      ) {
        // Find panel index
        const panelEls = Array.from(this.$el.querySelectorAll('.dynamic-accordion__panel'))
        const panelEl = event.target.closest('.dynamic-accordion__panel')
        const index = panelEls.indexOf(panelEl)
        if (index !== -1) {
          this.toggleReadMore(index)
        }
      }
    },

    /**
     * Extract plain text from HTML content
     * @param {String} html - HTML content
     * @returns {String}
     */
    getPlainText(html) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      return tempDiv.textContent || tempDiv.innerText || ''
    },

    /**
     * Truncate text at word boundary
     * @param {String} text - Text to truncate
     * @param {Number} maxLength - Maximum character length
     * @returns {String}
     */
    truncateAtWordBoundary(text, maxLength) {
      const words = text.split(/\s+/)
      let charCount = 0
      let wordCount = 0

      for (let index = 0; index < words.length; index++) {
        const nextLength = charCount + words[index].length + (index > 0 ? 1 : 0)

        if (nextLength > maxLength) {
          break
        }

        charCount = nextLength
        wordCount++
      }

      return words.slice(0, wordCount).join(' ')
    },

    /**
     * Process content truncation for a panel
     * @param {Object} panel - Panel object
     */
    processTruncation(panel) {
      if (!panel.content) {
        panel.needsTruncation = false
        panel.truncatedContent = ''
        return
      }

      const plainText = this.getPlainText(panel.content)

      if (plainText.length <= this.maxCharacters) {
        panel.needsTruncation = false
        panel.truncatedContent = panel.content
        return
      }

      panel.needsTruncation = true
      const truncatedText = this.truncateAtWordBoundary(plainText, this.maxCharacters)
      panel.truncatedContent = `
        <p>${truncatedText}...
          <span
            role="button"
            tabindex="0"
            class="dynamic-accordion__read-more-toggle text-body-s"
          >${this.$string('accessibility.read_more')}</span>
        </p>
      `
    },

    /**
     * Check if a panel is active using its index.
     * @param {Number} index - Index of a specific panel.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.panels[index].active
    },

    /**
     * Toggle a panel's active state.
     * - On mobile viewports, panels are toggled independently.
     * - On non-mobile viewports, opening one panel will close all other panels.
     * @param {Number} targetPanel - Index of a specific panel.
     */
    togglePanel(targetPanelIndex) {
      if (this.isMobile) {
        const targetPanel = this.panels[targetPanelIndex]
        targetPanel.active = !targetPanel.active
        return
      }

      if (this.isActive(targetPanelIndex)) {
        this.panels[targetPanelIndex].active = false
        return
      }

      this.panels.forEach((panel, index) => {
        if (this.isDescriptionPanel(panel)) {
          return
        }
        panel.active = targetPanelIndex === index
      })
    },

    /**
     * Toggle read more/less for a specific panel
     * @param {Number} index - Panel index
     */
    toggleReadMore(index) {
      this.panels[index].isExpanded = !this.panels[index].isExpanded
    },

    /**
     * Handle window resize
     */
    handleResize() {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        this.panels.forEach((panel) => {
          if (panel.useReadMore) {
            this.processTruncation(panel)
          }
        })
      }, 250)
    },

    /**
     * Get panel content for v-html binding
     * @param {Object} panel
     * @returns {String}
     */
    getPanelContent(panel) {
      return panel.isExpanded
        ? this.injectReadLessButton(panel.content)
        : panel.truncatedContent
    },
  },
}
</script>

<style lang="scss">
@import './dynamic-accordion';
</style>
